/** @format */

import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_PROPILOT_API_BASE || "http://localhost:8000";

export const PROPILOT_API_BASE = BASE_URL;

const propilotAxios = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 45000,
});

const uuidv4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const generateSessionId = () => uuidv4();

const buildHeaders = (token) => {
  const headers = { "X-Request-ID": uuidv4() };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

export async function sendChatMessage({
  sessionId,
  message,
  suggestionAction,
  token,
  adminMerchantId,
}) {
  const body = {
    session_id: sessionId,
    message,
  };
  if (suggestionAction) body.suggestion_action = suggestionAction;
  // Fresh TMS queries on large merchants (e.g. RTO-by-pincode over 60 days)
  // can legitimately take ~45–50s: Claude pipeline-gen + a wide Mongo
  // aggregation + Claude summary. The shared instance default (45s) was
  // aborting these just before the server responded, surfacing a false
  // "Cannot reach ProPilot". 120s gives heavy fresh queries headroom;
  // drill-downs (DuckDB, ~20ms) and other calls keep the instance default.
  const config = { headers: buildHeaders(token), timeout: 120000 };
  if (adminMerchantId !== undefined && adminMerchantId !== null && adminMerchantId !== "") {
    config.params = { merchant_list: adminMerchantId };
  }
  const res = await propilotAxios.post("/api/proship/chat", body, config);
  return res.data;
}

export async function analyzeDeeper({ sessionId, turnIndex, token }) {
  const res = await propilotAxios.post(
    "/api/proship/chat/analyze",
    { session_id: sessionId, turn_index: turnIndex },
    { headers: buildHeaders(token) }
  );
  return res.data;
}

// ── Answer feedback (thumbs up / down) ───────────────────────────────────────
//
// Persists a rating on a specific answer to /api/feedback. `rating` is
// "up" (correct/helpful) or "down" (wrong/unhelpful). Keyed server-side to
// (session_id, turn_index, user_email) so re-voting updates the same record.
export async function submitFeedback({
  sessionId,
  turnIndex,
  rating,
  question,
  comment,
  responseType,
  intent,
  token,
}) {
  const body = {
    session_id: sessionId,
    turn_index: turnIndex,
    rating,
  };
  if (question) body.question = question;
  if (comment) body.comment = comment;
  if (responseType) body.response_type = responseType;
  if (intent) body.intent = intent;
  const res = await propilotAxios.post("/api/proship/feedback", body, {
    headers: buildHeaders(token),
  });
  return res.data;
}

// ── Backing-data download (proof / full) ─────────────────────────────────────
//
// Generates a CSV of the current session's cached rows on demand and returns
// { ok, download_url, filename, record_count, mode, truncated, columns, row_cap }.
// mode "proof" → minimal [id, date, status] that proves the count;
// mode "full"  → all cached dimensions. Source-agnostic (ProShip/WMS/TMS).
export async function requestBackingData({ sessionId, mode, token }) {
  const res = await propilotAxios.post(
    "/api/proship/export/data",
    { session_id: sessionId, mode },
    { headers: buildHeaders(token), timeout: 60000 }
  );
  return res.data;
}

// ── Per-user analytics ───────────────────────────────────────────────────────
//
// Returns { ok, name, email, stats: { total_questions, questions_today,
// questions_this_week, questions_this_month, success_rate, helpful_count,
// not_helpful_count, top_topics: [{intent, count}], first_seen, last_active } }
// Powers the personalised greeting + activity cards on the landing screen.
export async function fetchMyAnalytics({ token }) {
  try {
    const res = await propilotAxios.get("/api/proship/analytics/me", {
      headers: buildHeaders(token),
      timeout: 8000,
    });
    return res.data || null;
  } catch (e) {
    return null;
  }
}

// ── Merchant facts (landing-page "Did you know?" rails) ─────────────────────
//
// Returns { ok, merchant_id, window_days, computed_at, stale, facts: [
// {key, rail, label, headline, sub, question, viz} ] }. Facts are computed
// server-side from the merchant's last-30-days shipments and cached per
// merchant (stale-while-revalidate, 0 Claude calls). The first request for
// a merchant after the cache expires can take a few seconds — hence the
// generous timeout. `adminMerchantId` mirrors sendChatMessage: admins pass
// the selected merchant, everyone else uses their token's merchant.
export async function fetchMyFacts({ token, adminMerchantId }) {
  try {
    const config = { headers: buildHeaders(token), timeout: 25000 };
    if (adminMerchantId !== undefined && adminMerchantId !== null && adminMerchantId !== "") {
      config.params = { merchant_list: adminMerchantId };
    }
    const res = await propilotAxios.get("/api/proship/facts", config);
    return res.data || null;
  } catch (e) {
    return null;
  }
}

export function resolveExportUrl(downloadUrl, fileId) {
  if (downloadUrl) {
    if (/^https?:\/\//.test(downloadUrl)) return downloadUrl;
    const sep = downloadUrl.startsWith("/") ? "" : "/";
    return `${BASE_URL}${sep}${downloadUrl}`;
  }
  if (fileId) return `${BASE_URL}/api/export/${fileId}`;
  return null;
}

export async function checkHealth() {
  try {
    const res = await propilotAxios.get("/health", { timeout: 5000 });
    return res.status === 200;
  } catch (e) {
    return false;
  }
}

// ── Example prompts (empty-state catalog) ────────────────────────────────────
//
// Returns { hint: {title, formula, example}, domains: [{key, label, examples: [{text, tags}]}] }
// The endpoint is read-only and unauthenticated (same surface as the other
// /api/options/* helpers). Used to render the empty-state hint card +
// domain-grouped starter prompts so Ops learn the [metric] by [dimension]
// for [time range] pattern.
export async function fetchExamplePrompts() {
  try {
    const res = await propilotAxios.get("/api/options/example-prompts", {
      timeout: 8000,
    });
    return res.data || { hint: null, domains: [] };
  } catch (e) {
    return { hint: null, domains: [] };
  }
}

export const AUTH_FAILURE_CODES = new Set([
  "AUTH_MISSING",
  "AUTH_MALFORMED",
  "AUTH_BAD_SIGNATURE",
  "AUTH_EXPIRED",
]);

const AUTH_ERROR_MESSAGES = {
  AUTH_MISSING: "Your session has expired. Redirecting to login…",
  AUTH_MALFORMED: "Your session is invalid. Redirecting to login…",
  AUTH_BAD_SIGNATURE: "Your session is invalid. Redirecting to login…",
  AUTH_EXPIRED: "Your session has expired. Redirecting to login…",
  AUTH_PANEL_FORBIDDEN:
    "ProPilot insights aren't available for this account type.",
  AUTH_ACCESS_DENIED:
    "ProPilot requires the MERCHANT_ADMIN module. Contact your administrator.",
  AUTH_NO_MERCHANT:
    "Please pick a merchant in ProShip first, then open ProPilot.",
  AUTH_NOT_CONFIGURED:
    "ProPilot is temporarily unavailable. Please try again later.",
  ADMIN_MERCHANT_REQUIRED:
    "Please switch to a merchant from the top-right menu before using ProPilot.",
  ADMIN_PARAM_NOT_PERMITTED:
    "Your account isn't allowed to query other merchants.",
};

export function parseChatError(error) {
  const detail = error?.response?.data?.detail;
  const errorCode = detail?.error_code;
  const status = error?.response?.status;

  let userMessage;
  if (errorCode && AUTH_ERROR_MESSAGES[errorCode]) {
    userMessage = AUTH_ERROR_MESSAGES[errorCode];
  } else if (typeof detail === "string") {
    userMessage = detail;
  } else if (error?.response?.data?.user_message) {
    userMessage = error.response.data.user_message;
  } else if (status >= 500) {
    userMessage = "ProPilot is temporarily unavailable. Please try again.";
  } else if (error?.code === "ERR_NETWORK" || !error?.response) {
    userMessage =
      "Cannot reach ProPilot. Check your connection and try again.";
  } else {
    userMessage = error?.message || "Something went wrong. Please try again.";
  }

  return {
    errorCode,
    userMessage,
    isAuthFailure: !!errorCode && AUTH_FAILURE_CODES.has(errorCode),
  };
}
